import SentryService from '@/services/sentry/Sentry.service';

export interface EventData {
  inPersonEventSessions: boolean;
  eventPeriods: Array<{
    startDate: string;
    endDate: string;
  }>;
}

export type EnvironmentName = 'local' | 'staging' | 'production' | 'web';

export interface EnvironmentConfig {
  auth0: {
    audience: string;
    clientId: string;
    domain: string;
  };
  cloudinary: {
    apiKey: string;
    cloudName: string;
  };
  name: EnvironmentName;
  sentry: {
    dsn: string;
  };
  server: {
    baseUrl: string;
  };
  stripe: {
    publishableKey: string;
  };
  feature: {
    editProfile: boolean;
  };
  sendbird: {
    applicationId: string;
  };
}

// Only web environment is loaded from the .env
// All env vars should be prefixed with EXPO_PUBLIC_ and accessed directly
// from process.env due to constraints with Expo web export
const AUTH0_AUDIENCE = process.env.EXPO_PUBLIC_AUTH0_AUDIENCE ?? '';
const AUTH0_CLIENT_ID = process.env.EXPO_PUBLIC_AUTH0_CLIENT_ID ?? '';
const AUTH0_DOMAIN = process.env.EXPO_PUBLIC_AUTH0_DOMAIN ?? '';
const CLOUDINARY_API_KEY = process.env.EXPO_PUBLIC_CLOUDINARY_API_KEY ?? '';
const CLOUDINARY_CLOUD_NAME =
  process.env.EXPO_PUBLIC_CLOUDINARY_CLOUD_NAME ?? '';
const ENVIRONMENT_NAME = process.env.EXPO_PUBLIC_ENVIRONMENT_NAME ?? '';
const SENTRY_DSN = process.env.EXPO_PUBLIC_SENTRY_DSN ?? '';
const SERVER_BASE_URL = process.env.EXPO_PUBLIC_SERVER_BASE_URL ?? '';
const STRIPE_PUBLISHABLE_KEY =
  process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? '';
const SENDBIRD_APPLICATION_ID =
  process.env.EXPO_PUBLIC_SENDBIRD_APPLICATION_ID ?? '';

// Feature flags
const FEATURE_EDIT_PROFILE =
  process.env.EXPO_PUBLIC_FEATURE_EDIT_PROFILE === 'true';

export const webEnvironment: EnvironmentConfig = {
  auth0: {
    audience: AUTH0_AUDIENCE,
    clientId: AUTH0_CLIENT_ID,
    domain: AUTH0_DOMAIN,
  },
  cloudinary: {
    apiKey: CLOUDINARY_API_KEY,
    cloudName: CLOUDINARY_CLOUD_NAME,
  },
  name: ENVIRONMENT_NAME as EnvironmentName,
  sentry: {
    dsn: SENTRY_DSN,
  },
  server: {
    baseUrl: SERVER_BASE_URL,
  },
  stripe: {
    publishableKey: STRIPE_PUBLISHABLE_KEY,
  },
  feature: {
    editProfile: FEATURE_EDIT_PROFILE,
  },
  sendbird: {
    applicationId: SENDBIRD_APPLICATION_ID,
  },
};

// Dynamic configuration loading function
export async function getEventConfiguration(): Promise<EventData> {
  try {
    // Use EventService to fetch event data
    const response = await fetch(`${SERVER_BASE_URL}/api/event/eventData`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const eventData = await response.json();
    return eventData;
  } catch (error) {
    SentryService.captureError(
      'Failed to load event configuration, using defaults:',
      error,
    );
    return {
      inPersonEventSessions: false,
      eventPeriods: [
        {
          startDate: '2025-09-19',
          endDate: '2025-09-21',
        },
        {
          startDate: '2025-09-26',
          endDate: '2025-09-28',
        },
      ],
    };
  }
}
